/**
 * RBAC catalog — role keys and permission keys used across the system.
 *
 * Permissions follow the pattern: `<resource>.<action>`.
 * Action set: read, create, update, delete, approve, post, reverse, export, admin.
 *
 * Roles map to permission sets via the `rolePermissions` table at runtime.
 * This file is the single source of truth for valid keys.
 */

export const ROLE_KEYS = {
  OWNER: "owner",
  ADMIN: "admin",
  FINANCE: "finance",
  FACTORY_MANAGER: "factory_manager",
  /**
   * Office Manager — runs the front-office: dealer relationships, invoicing,
   * payments, sales-order approval. Has visibility into operations but
   * deliberately CANNOT see landed cost, margins, or the GL — so they can't
   * back into "what we paid" or "what we're earning".
   */
  OFFICE_MANAGER: "office_manager",
  SALES: "sales",
  WAREHOUSE: "warehouse",
  DEALER: "dealer",
} as const;

export type RoleKey = (typeof ROLE_KEYS)[keyof typeof ROLE_KEYS];

export const PERMISSION_KEYS = {
  // Catalog
  PRODUCTS_READ: "products.read",
  PRODUCTS_WRITE: "products.write",
  PRODUCTS_DELETE: "products.delete",
  // BOM
  BOMS_READ: "boms.read",
  BOMS_WRITE: "boms.write",
  BOMS_ACTIVATE: "boms.activate",
  // Manufacturing — work orders
  WORK_ORDERS_READ: "workOrders.read",
  WORK_ORDERS_WRITE: "workOrders.write",
  WORK_ORDERS_START: "workOrders.start",
  WORK_ORDERS_COMPLETE: "workOrders.complete",
  WORK_ORDERS_CANCEL: "workOrders.cancel",
  // Inventory
  INVENTORY_READ: "inventory.read",
  INVENTORY_ADJUST: "inventory.adjust",
  INVENTORY_TRANSFER: "inventory.transfer",
  // Procurement
  SUPPLIERS_READ: "suppliers.read",
  SUPPLIERS_WRITE: "suppliers.write",
  PURCHASE_ORDERS_READ: "purchaseOrders.read",
  PURCHASE_ORDERS_WRITE: "purchaseOrders.write",
  PURCHASE_ORDERS_APPROVE: "purchaseOrders.approve",
  // Imports
  LC_READ: "lc.read",
  LC_WRITE: "lc.write",
  SHIPMENTS_READ: "shipments.read",
  SHIPMENTS_WRITE: "shipments.write",
  LANDED_COST_POST: "landedCost.post",
  // Sales
  SALES_ORDERS_READ: "salesOrders.read",
  SALES_ORDERS_WRITE: "salesOrders.write",
  SALES_ORDERS_APPROVE: "salesOrders.approve",
  // Invoicing
  INVOICES_READ: "invoices.read",
  INVOICES_WRITE: "invoices.write",
  INVOICES_VOID: "invoices.void",
  PAYMENTS_RECORD: "payments.record",
  // Pricing
  PRICING_READ: "pricing.read",
  PRICING_WRITE: "pricing.write",
  PRICING_SETTINGS: "pricing.settings",
  // Dealers
  DEALERS_READ: "dealers.read",
  DEALERS_WRITE: "dealers.write",
  // Points
  POINTS_READ: "points.read",
  POINTS_AWARD: "points.award",
  POINTS_REDEEM_APPROVE: "points.redeemApprove",
  // Market Visits
  VISITS_READ: "visits.read",
  VISITS_WRITE: "visits.write",
  VISITS_APPROVE: "visits.approve",
  // Finance
  FINANCE_READ: "finance.read",
  FINANCE_WRITE: "finance.write",
  LOANS_WRITE: "loans.write",
  FX_WRITE: "fx.write",
  // General Ledger / Chart of Accounts / Journal Entries / Reports
  ACCOUNTS_READ: "accounts.read",
  ACCOUNTS_WRITE: "accounts.write",
  JOURNAL_READ: "journal.read",
  JOURNAL_WRITE: "journal.write",
  JOURNAL_POST: "journal.post",
  JOURNAL_REVERSE: "journal.reverse",
  REPORTS_FINANCIAL: "reports.financial",
  // Opening balance (one-shot seed when company adopts BizOP mid-life)
  OPENING_READ: "opening.read",
  OPENING_WRITE: "opening.write",
  OPENING_CLOSE: "opening.close",
  // People & Access
  USERS_READ: "users.read",
  USERS_WRITE: "users.write",
  ROLES_WRITE: "roles.write",
  // Workforce / HR — employees, attendance, payroll
  EMPLOYEES_READ: "employees.read",
  EMPLOYEES_WRITE: "employees.write",
  EMPLOYEES_TERMINATE: "employees.terminate",
  ATTENDANCE_READ: "attendance.read",
  ATTENDANCE_WRITE: "attendance.write",
  PAYROLL_READ: "payroll.read",
  PAYROLL_WRITE: "payroll.write",
  PAYROLL_APPROVE: "payroll.approve",
  // Master data — branches & territories
  BRANCHES_READ: "branches.read",
  BRANCHES_WRITE: "branches.write",
  DEALER_AREAS_READ: "dealerAreas.read",
  DEALER_AREAS_WRITE: "dealerAreas.write",
  TAX_RATES_WRITE: "taxRates.write",
  UNITS_WRITE: "units.write",
  // Platform
  AUDIT_READ: "audit.read",
  SETTINGS_WRITE: "settings.write",
  REPORTS_READ: "reports.read",
  DASHBOARD_VIEW_ALL: "dashboard.viewAll",
  DASHBOARD_VIEW_FINANCE: "dashboard.viewFinance",
  // System — admin-exclusive destructive / maintenance ops
  SYSTEM_CLEAR_DATA: "system.clearData",
  SYSTEM_DELETE_DATA: "system.deleteData",
  SYSTEM_MAINTENANCE: "system.maintenance",
  SYSTEM_DEV_TOOLS: "system.devTools",
} as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[keyof typeof PERMISSION_KEYS];

/** Default permission grants per role. Used by seed.ts to populate rolePermissions. */
export const DEFAULT_ROLE_PERMISSIONS: Record<RoleKey, PermissionKey[]> = {
  [ROLE_KEYS.ADMIN]: Object.values(PERMISSION_KEYS),
  [ROLE_KEYS.OWNER]: Object.values(PERMISSION_KEYS).filter(
    (p) =>
      p !== PERMISSION_KEYS.ROLES_WRITE &&
      p !== PERMISSION_KEYS.SETTINGS_WRITE &&
      p !== PERMISSION_KEYS.AUDIT_READ &&
      p !== PERMISSION_KEYS.SYSTEM_CLEAR_DATA &&
      p !== PERMISSION_KEYS.SYSTEM_DELETE_DATA &&
      p !== PERMISSION_KEYS.SYSTEM_MAINTENANCE &&
      p !== PERMISSION_KEYS.SYSTEM_DEV_TOOLS,
  ),
  [ROLE_KEYS.FINANCE]: [
    PERMISSION_KEYS.PRODUCTS_READ,
    PERMISSION_KEYS.INVENTORY_READ,
    PERMISSION_KEYS.SUPPLIERS_READ,
    PERMISSION_KEYS.PURCHASE_ORDERS_READ,
    PERMISSION_KEYS.LC_READ,
    PERMISSION_KEYS.LC_WRITE,
    PERMISSION_KEYS.SHIPMENTS_READ,
    PERMISSION_KEYS.LANDED_COST_POST,
    PERMISSION_KEYS.SALES_ORDERS_READ,
    PERMISSION_KEYS.INVOICES_READ,
    PERMISSION_KEYS.INVOICES_WRITE,
    PERMISSION_KEYS.INVOICES_VOID,
    PERMISSION_KEYS.PAYMENTS_RECORD,
    PERMISSION_KEYS.PRICING_READ,
    PERMISSION_KEYS.PRICING_WRITE,
    PERMISSION_KEYS.PRICING_SETTINGS,
    PERMISSION_KEYS.DEALERS_READ,
    PERMISSION_KEYS.POINTS_READ,
    PERMISSION_KEYS.FINANCE_READ,
    PERMISSION_KEYS.FINANCE_WRITE,
    PERMISSION_KEYS.LOANS_WRITE,
    PERMISSION_KEYS.FX_WRITE,
    PERMISSION_KEYS.ACCOUNTS_READ,
    PERMISSION_KEYS.ACCOUNTS_WRITE,
    PERMISSION_KEYS.JOURNAL_READ,
    PERMISSION_KEYS.JOURNAL_WRITE,
    PERMISSION_KEYS.JOURNAL_POST,
    PERMISSION_KEYS.JOURNAL_REVERSE,
    PERMISSION_KEYS.REPORTS_FINANCIAL,
    PERMISSION_KEYS.OPENING_READ,
    PERMISSION_KEYS.OPENING_WRITE,
    PERMISSION_KEYS.OPENING_CLOSE,
    PERMISSION_KEYS.AUDIT_READ,
    PERMISSION_KEYS.REPORTS_READ,
    PERMISSION_KEYS.DASHBOARD_VIEW_ALL,
    PERMISSION_KEYS.DASHBOARD_VIEW_FINANCE,
    PERMISSION_KEYS.EMPLOYEES_READ,
    PERMISSION_KEYS.ATTENDANCE_READ,
    PERMISSION_KEYS.PAYROLL_READ,
    PERMISSION_KEYS.PAYROLL_WRITE,
    PERMISSION_KEYS.PAYROLL_APPROVE,
    PERMISSION_KEYS.BRANCHES_READ,
    PERMISSION_KEYS.DEALER_AREAS_READ,
  ],
  [ROLE_KEYS.FACTORY_MANAGER]: [
    PERMISSION_KEYS.PRODUCTS_READ,
    PERMISSION_KEYS.PRODUCTS_WRITE,
    PERMISSION_KEYS.BOMS_READ,
    PERMISSION_KEYS.BOMS_WRITE,
    PERMISSION_KEYS.BOMS_ACTIVATE,
    PERMISSION_KEYS.WORK_ORDERS_READ,
    PERMISSION_KEYS.WORK_ORDERS_WRITE,
    PERMISSION_KEYS.WORK_ORDERS_START,
    PERMISSION_KEYS.WORK_ORDERS_COMPLETE,
    PERMISSION_KEYS.WORK_ORDERS_CANCEL,
    PERMISSION_KEYS.INVENTORY_READ,
    PERMISSION_KEYS.INVENTORY_ADJUST,
    PERMISSION_KEYS.INVENTORY_TRANSFER,
    PERMISSION_KEYS.SUPPLIERS_READ,
    PERMISSION_KEYS.PURCHASE_ORDERS_READ,
    PERMISSION_KEYS.PURCHASE_ORDERS_WRITE,
    PERMISSION_KEYS.SHIPMENTS_READ,
    PERMISSION_KEYS.SHIPMENTS_WRITE,
    PERMISSION_KEYS.ACCOUNTS_READ,
    PERMISSION_KEYS.JOURNAL_READ,
    PERMISSION_KEYS.DASHBOARD_VIEW_ALL,
  ],
  [ROLE_KEYS.OFFICE_MANAGER]: [
    // Catalog + inventory visibility (read-only)
    PERMISSION_KEYS.PRODUCTS_READ,
    PERMISSION_KEYS.INVENTORY_READ,
    PERMISSION_KEYS.WORK_ORDERS_READ,
    // Procurement visibility — can see supplier directory + PO list, but
    // cannot manage POs or see landed-cost economics.
    PERMISSION_KEYS.SUPPLIERS_READ,
    PERMISSION_KEYS.PURCHASE_ORDERS_READ,
    PERMISSION_KEYS.SHIPMENTS_READ,
    // Sales floor — full operational ownership.
    PERMISSION_KEYS.SALES_ORDERS_READ,
    PERMISSION_KEYS.SALES_ORDERS_WRITE,
    PERMISSION_KEYS.SALES_ORDERS_APPROVE,
    PERMISSION_KEYS.INVOICES_READ,
    PERMISSION_KEYS.INVOICES_WRITE,
    PERMISSION_KEYS.INVOICES_VOID,
    PERMISSION_KEYS.PAYMENTS_RECORD,
    PERMISSION_KEYS.DEALERS_READ,
    PERMISSION_KEYS.DEALERS_WRITE,
    PERMISSION_KEYS.POINTS_READ,
    PERMISSION_KEYS.POINTS_AWARD,
    PERMISSION_KEYS.VISITS_READ,
    PERMISSION_KEYS.VISITS_WRITE,
    // Pricing — read only. Cannot manipulate margins.
    PERMISSION_KEYS.PRICING_READ,
    // Master data (read-only territory + branches)
    PERMISSION_KEYS.BRANCHES_READ,
    PERMISSION_KEYS.DEALER_AREAS_READ,
    // People — sees colleagues' directory + attendance, no payroll/HR power.
    PERMISSION_KEYS.EMPLOYEES_READ,
    PERMISSION_KEYS.ATTENDANCE_READ,
    // Dashboards + ops reports — but NOT finance widgets/P&L.
    PERMISSION_KEYS.DASHBOARD_VIEW_ALL,
    PERMISSION_KEYS.REPORTS_READ,
    // EXPLICITLY NOT GRANTED (so the gate is auditable here):
    //   LC_READ / LC_WRITE                 → can't see open LCs
    //   LANDED_COST_POST                    → can't post or see landed cost
    //   ACCOUNTS_READ / JOURNAL_READ        → no GL visibility
    //   FINANCE_READ / FINANCE_WRITE        → no finance dashboards
    //   LOANS_WRITE / FX_WRITE              → no loans, no FX
    //   REPORTS_FINANCIAL                   → no P&L, BS, cash-flow, trial balance
    //   PRICING_WRITE / PRICING_SETTINGS    → can't tweak margins
    //   PURCHASE_ORDERS_WRITE / APPROVE     → no PO control
    //   DASHBOARD_VIEW_FINANCE              → no cash-position widget
    //   USERS_WRITE / ROLES_WRITE           → no admin powers
    //   PAYROLL_*                           → no payroll
    //   OPENING_*                           → no opening balance edits
  ],
  [ROLE_KEYS.SALES]: [
    PERMISSION_KEYS.PRODUCTS_READ,
    PERMISSION_KEYS.INVENTORY_READ,
    PERMISSION_KEYS.WORK_ORDERS_READ,
    PERMISSION_KEYS.SALES_ORDERS_READ,
    PERMISSION_KEYS.SALES_ORDERS_WRITE,
    PERMISSION_KEYS.SALES_ORDERS_APPROVE,
    PERMISSION_KEYS.INVOICES_READ,
    PERMISSION_KEYS.PRICING_READ,
    PERMISSION_KEYS.DEALERS_READ,
    PERMISSION_KEYS.DEALERS_WRITE,
    PERMISSION_KEYS.POINTS_READ,
    PERMISSION_KEYS.POINTS_AWARD,
    PERMISSION_KEYS.VISITS_READ,
    PERMISSION_KEYS.VISITS_WRITE,
    PERMISSION_KEYS.VISITS_APPROVE,
    PERMISSION_KEYS.ACCOUNTS_READ,
    PERMISSION_KEYS.JOURNAL_READ,
    PERMISSION_KEYS.DASHBOARD_VIEW_ALL,
    PERMISSION_KEYS.BRANCHES_READ,
    PERMISSION_KEYS.DEALER_AREAS_READ,
    PERMISSION_KEYS.DEALER_AREAS_WRITE,
  ],
  [ROLE_KEYS.WAREHOUSE]: [
    PERMISSION_KEYS.PRODUCTS_READ,
    PERMISSION_KEYS.INVENTORY_READ,
    PERMISSION_KEYS.INVENTORY_ADJUST,
    PERMISSION_KEYS.INVENTORY_TRANSFER,
    PERMISSION_KEYS.SHIPMENTS_READ,
    PERMISSION_KEYS.SHIPMENTS_WRITE,
    PERMISSION_KEYS.SALES_ORDERS_READ,
    PERMISSION_KEYS.ACCOUNTS_READ,
    PERMISSION_KEYS.JOURNAL_READ,
  ],
  [ROLE_KEYS.DEALER]: [
    PERMISSION_KEYS.PRODUCTS_READ,
    PERMISSION_KEYS.SALES_ORDERS_READ,
    PERMISSION_KEYS.SALES_ORDERS_WRITE,
    PERMISSION_KEYS.INVOICES_READ,
    PERMISSION_KEYS.POINTS_READ,
    PERMISSION_KEYS.VISITS_READ,
    PERMISSION_KEYS.VISITS_WRITE,
  ],
};
